import { useState } from 'react';
// @mui
import Typography from '@mui/material/Typography';
// components
import {
  IEditForm,
  CasEditForm,
  CasRHFSwitch,
  CasRHFMultilineTextField,
  CasDisplayField,
  CasStackRow,
} from 'src/compass/shared/components';
import {
  CasAsOfContainer,
  CasAsOfMultiRowTable,
  EntityInfoLRPAccounts,
  useDefaultAsOfValues,
} from 'src/compass/application/commissions';
import { CasRHFRiskManagerSelect } from 'src/compass/application/general/components/CasRiskManagerSelect';
import { CasRHFRiskWithSalesManagerSelect } from 'src/compass/application/general/components/CasRiskWithSalesManagerSelect';

// hooks
import { formatDate, DateFormat } from 'src/compass/shared/util/casMoment';

// schema
import { LRPAccountsSchema } from './LRPAccountsSchema';
import ScheduleSelect from './ScheduleSelect';
import { RHFAdditCompensationSelect } from '../AdditCompensationSelect';
import SourceEmployeeAndTypeFields from '../../Cme/SourceEmployeeAndTypeFields';

// ----------------------------------------------------------------------

export default function LRPAccountForm({ row, onSaved }: IEditForm) {
  const { id } = row || {};

  const [asofReadonly, setAsofReadonly] = useState(false);
  const [servicingAgentId, setServicingAgentId] = useState();

  const defaultValues = useDefaultAsOfValues(row);

  return (
    <CasEditForm
      defaultValues={defaultValues}
      onSaved={onSaved}
      entity={EntityInfoLRPAccounts.entity}
      schema={LRPAccountsSchema}
    >
      <CasStackRow>
        <CasDisplayField label="Name" value={row?.company} />
        <CasDisplayField label="Account#" value={row?.accountNumber} />
        <CasDisplayField label="Date Open" value={formatDate(row?.opened, DateFormat.MDY)} />
        <CasDisplayField label="Agency" value={row?.agencyName} />
        <CasDisplayField label="Handoff" value={formatDate(row?.handoffAsOfDate, DateFormat.MDY)} />
      </CasStackRow>
      <CasStackRow>
        <CasDisplayField label="Customer" value={row?.customer} />
      </CasStackRow>
      <CasRHFMultilineTextField name="notes" label="Notes" />
      <CasStackRow justifyContent="space-between">
        <CasRHFSwitch
          name="isSuspended"
          label="Suspended"
          helperText="Don't pay until final payment received"
        />
        <CasDisplayField label="Agent per Hudson" value={row?.agentPerHudson} />
      </CasStackRow>
      <CasAsOfContainer
        entityTitle='LRP Account AsOf'
        entityId={id}
        deleteChildEntity="Ins/Account/AsOf"
        initialAsOfList={row?.asOfs}
        initialAsOf={defaultValues.asOf}
        readOnly={asofReadonly}
        setReadOnly={setAsofReadonly}
      >
        <CasRHFRiskManagerSelect
          name="asOf.servicingAgentId"
          label="Relationship Owner"
          onChange={(_, value) => setServicingAgentId(value)}
          disabled={asofReadonly}
        />
        <ScheduleSelect
          name="asOf.insCommScheduleId"
          label="Commission Schedule"
          employeeId={servicingAgentId}
          disabled={!servicingAgentId || asofReadonly}
        />
        <SourceEmployeeAndTypeFields asofReadonly={asofReadonly} />
        <CasAsOfMultiRowTable
          readOnly={asofReadonly}
          name="asOf.asOfAdditComps"
          rowCountMode={0}
          columns={[
            {
              type: 'component',
              label: 'Addit Comp Emp',
              name: 'employeeId',
              fullWidth: true,
              component: CasRHFRiskWithSalesManagerSelect,
            },
            {
              type: 'component',
              label: 'Comp Type',
              name: 'insCompTypeId',
              fullWidth: true,
              component: RHFAdditCompensationSelect,
            },
          ]}
        />
      </CasAsOfContainer>
      <Typography
        variant="caption"
        sx={{
          fontStyle: 'italic',
          color: 'text.disabled',
        }}
      >
        AccountId:{id}
      </Typography>
    </CasEditForm>
  );
}
